import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiVariable } from "../../../api/apiVariable";
import axios from "axios";
import useAuth from "../../../hooks/useAuth";
import { useState } from "react";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { Button } from "@mui/material";
import { useParams } from "react-router-dom";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const AssignTrailerButton = ({
  buttonLocation,
}: {
  buttonLocation: string;
}) => {
  const { token } = useAuth();
  const [autocompleteData, setAutocompleteData] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const assetId = useParams().id;
  const queryClient = useQueryClient();

  const isTrailerLocation = buttonLocation === "trailer";
  const apiEndpoint = isTrailerLocation ? "getAllTrucks" : "getAllTrailers";
  const labelFormatter = (item: any) =>
    isTrailerLocation
      ? `${item.unitNumber} | ${item.make} ${item.model} ${item.year}`
      : `${item.unitNumber} | ${item.make} ${item.model} ${item.year}`;

  useQuery({
    queryKey: [isTrailerLocation ? "allTrucks" : "allTrailers"],
    queryFn: async () => {
      const res = await axios.get(`${apiVariable}/${apiEndpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { areActive: true },
      });
      const formattedData = res.data.map((item: any) => ({
        label: labelFormatter(item),
        id: item.id.toString(),
      }));
      setAutocompleteData(formattedData);
      return res.data;
    },
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/assignTrailerToTruck`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["assignedTrailers"] });
      queryClient.invalidateQueries({ queryKey: ["assignedTrucks"] });

      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit truck");
    },
  });

  const submitHandler = (data: any) => {
    if (buttonLocation === "truck") {
      data.trailerId = getId(data.trailerId);
      data.truckId = getId(assetId);
    } else {
      data.truckId = getId(data.truckId);
      data.trailerId = getId(assetId);
    }
    mutation.mutate(data);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        sx={{
          mr: 2,
        }}
      >
        {isTrailerLocation ? "Assign truck" : "Assign trailer"}
      </Button>
      <GenericFormPopup
        formFields={[
          {
            name: isTrailerLocation ? "truckId" : "trailerId",
            label: isTrailerLocation ? "Truck" : "Trailer",
            type: "combo",
            dataKey: isTrailerLocation ? "trucks" : "trailers",
          },
        ]}
        initialValues={{ trailerId: "", truckId: "" }}
        title={
          isTrailerLocation
            ? "Assign Truck to Trailer"
            : "Assign Trailer to Truck"
        }
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        autocompleteData={{
          [isTrailerLocation ? "trucks" : "trailers"]: autocompleteData,
        }}
        loading={mutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage={
          isTrailerLocation
            ? "Truck assigned successfully!"
            : "Trailer assigned successfully!"
        }
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default AssignTrailerButton;
